import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { scriptsApi } from '@/services/api/scripts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Settings, ArrowRight } from 'lucide-react'
import { LoadingPage } from '@/components/ui/loading-spinner'
import { showErrorToast } from '@/utils/error-handler'

export default function BacktestConfigPage() {
  const navigate = useNavigate()

  const { data: scriptsData, isLoading, error } = useQuery({
    queryKey: ['scripts'],
    queryFn: async () => {
      const result = await scriptsApi.list()
      return result.scripts
    },
  })
  
  const scripts = scriptsData || []

  if (isLoading) {
    return <LoadingPage />
  }

  if (error) {
    showErrorToast(error, 'Failed to load scripts')
    return (
      <div className="container mx-auto px-4 py-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">Failed to load scripts. Please try again.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const handleConfigure = (scriptId: string) => {
    navigate(`/scripts/${scriptId}`, { state: { openBacktestConfig: true } })
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Configure Backtest</h1>
        <p className="text-muted-foreground">
          Select a script to configure its backtest parameters, including entry/exit signals, stop loss, take profit, and more.
        </p>
      </div>

      {!scripts || scripts.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Settings className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No scripts available</h3>
              <p className="text-muted-foreground mb-4">
                Create a script first to configure backtest settings.
              </p>
              <Button onClick={() => navigate('/scripts/new')}>
                Create Script
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {scripts.map((script => (
            <Card key={script.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <CardTitle className="text-lg">{script.name || 'Untitled Script'}</CardTitle>
                <CardDescription className="line-clamp-2">
                  {script.description || 'No description'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {script.compiledSql ? (
                      <span className="text-green-600">Compiled</span>
                    ) : (
                      <span className="text-yellow-600">Not compiled</span>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleConfigure(script.id)}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Configure
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )))}
        </div>
      )}
    </div>
  )
}
